'use client';

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { AlertTriangle, Clock } from 'lucide-react';
import { PageHeader } from './page-header';
import { Badge } from '../../components/ui/badge';
import { Resource } from '../../types/resource';
import { Page } from '../../types/page';
import { cn, getCurrencySymbol } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { markdownToHtml } from '../../lib/markdown-to-html';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';

interface PageProps {
  page: Page;
  onSelect: (resource: Resource) => void;
}

export function ResourceList({ page, onSelect }: PageProps) {
  const publicResources = page.resources
    .filter((r: any) => r.settings?.privacy?.type !== 'private')
    .sort((a, b) => a.rank - b.rank);

  return (
    <div className="p-6">
      <PageHeader
        logo={page.logo}
        name={page.name}
        description={page.description}
      />
      <div
        className={cn(
          'max-w-4xl mx-auto grid gap-6',
          publicResources.length <= 1
            ? 'grid-cols-1 place-items-center'
            : 'grid-cols-1 md:grid-cols-2'
        )}
      >
        {publicResources.length ? (
          publicResources.map((resource) => (
            <Card
              key={resource.resource_id}
              className={cn(
                'min-w-auto md:min-w-md max-w-md shadow-xl hover:shadow-2xl transition-shadow flex flex-col h-full border-l-[4px]',
                resource.is_enabled
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed opacity-50'
              )}
              style={{ borderLeftColor: resource.color }}
              onClick={
                resource.is_enabled ? () => onSelect(resource) : undefined
              }
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-xl line-clamp-2">
                    {resource.name}
                  </CardTitle>
                  {resource.prices?.length > 0 &&
                    resource.payment?.is_enabled && (
                      <span className='text-nowrap'>
                        {getCurrencySymbol(resource.prices[0].currency)}{' '}
                        {resource.prices[0].amount}
                      </span>
                    )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2 flex-grow pb-4">
                <div className="flex items-start gap-2">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div className="flex gap-2">
                      {resource.durations?.length > 0 && (
                        <Badge variant="secondary">
                          {resource.durations[0].value}{' '}
                          {resource.durations[0].type.charAt(0)}
                        </Badge>
                      )}
                      {resource.durations?.length > 1 && (
                        <Badge variant="secondary">
                          +{resource.durations.length - 1} more
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Badge variant="secondary">
                    {resource.event_type?.replaceAll('_', ' ') || resource.type}
                  </Badge>
                </div>
                <div
                  className="text-sm text-muted-foreground line-clamp-2"
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtml(resource.description),
                  }}
                ></div>
              </CardContent>

              <CardFooter className="border-t py-2">
                <Button
                  size="sm"
                  className="w-full"
                  variant={'outline'}
                  disabled={resource.is_enabled === false}
                >
                  Book Now
                </Button>
              </CardFooter>
            </Card>
          ))
        ) : (
          <Alert variant={'destructive'} className="mt-4 mx-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              This page doesn't have a bookable resource yet. Are you the owner?
            </AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5 space-y-2 mt-2">
                <li>
                  <a
                    href="https://app.dayschedule.com/resources"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-blue-600"
                  >
                    Go to Resources
                  </a>{' '}
                  &gt; Add a bookable resource (event, service, webinar etc)
                </li>
                <li>
                  Ensure that the{' '}
                  <a
                    href="https://dayschedule.com/docs/t/how-to-show-hide-an-event-on-scheduling-page/363"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-blue-600"
                  >
                    resource is enabled
                  </a>{' '}
                  and privacy is set to public
                </li>
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}
